import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GETSearchType, POSTCreateSearchType } from 'src/types'
import close from '@assets/images/close.svg'
import pencil from '@assets/images/pencil.svg'
import sortArrowUp from '@assets/images/sort-arrow-up.svg'
import { useFetchSearchFiltersQuery } from '@entities/search'
import {
  Button,
  ConfirmModal,
  TextField,
  TextFieldProps,
} from '@microservices/wiskey-react-components'
import {
  Box,
  Divider,
  Grid,
  ListItemButton,
  ListItemText,
  Radio,
  styled,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { Sort } from '@widgets/SearchAssistant/lib/constants'
import { useCreateSearchFilterManager } from '@widgets/SearchAssistant/model/useCreateSearchFilterManager'
import { useDeleteSearchFilterManager } from '@widgets/SearchAssistant/model/useDeleteSearchFilterManager'
import { useSortSearchFilterManager } from '@widgets/SearchAssistant/model/useSortSearchFilterManager'
import { useUpdateDefaultSearchFilterManager } from '@widgets/SearchAssistant/model/useUpdateDefaultSearchFilterManager'
import { useUpdateSearchFilterTitleManager } from '@widgets/SearchAssistant/model/useUpdateSearchFilterTitleManager'
import { SearchAssistantPresenter } from '@widgets/SearchAssistant/presenter/search-assistant.presenter'
import { getDefaultSearchFilter } from '@widgets/SearchAssistant/store/search-assistant.slice'

import { AppLoader } from '@components/AppLoader'
import { CustomSvgIcon } from '@components/CustomSvgIcon'

import { useAppSelector, usePrompt } from '@hooks'
import { buttonStylesByMode } from '@helpers'
import { FLEXIBLE } from '@constants'

export enum FavoritesFiltersTableMode {
  VIEW = 'VIEW',
  EDIT = 'EDIT',
  CREATE = 'CREATE',
}

interface SearchAssistantFilterManagerProps {
  dialogId: string
  createSearchPayload?: POSTCreateSearchType
  mode: FavoritesFiltersTableMode
  entityObjectCode: string | undefined
  searchAssistantId: number | string | undefined
  searchAssistantKey: string
  onChangeDirty?: (value: boolean) => void
}

export const StyledButton = styled(Button)<{ theme: Theme }>(({ theme, style }) => ({
  ...buttonStylesByMode(theme),
  boxShadow: theme.palette.boxShadow.filterButton,
  fontFamily: 'Montserrat Regular',
  fontSize: 11,
  lineHeight: '16px',
  padding: '0.5px 7.5px',
  '& .MuiButton-startIcon ': {
    pl: '7.5px',
  },
  height: 17,
  marginRight: 4,
  ...style,
}))

export const appLoaderSx: SxProps<Theme> = {
  position: 'absolute',
  width: 'fit-content',
  height: 'fit-content',
  right: '10px',
}

const rowSx = {
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
}

const EditTextField: React.FC<TextFieldProps> = props => {
  return (
    <TextField
      fullWidth
      size='small'
      sx={{
        // borderRadius: 0,
        backgroundColor: theme => theme.palette.common.white,
        '& > .MuiInputBase-root': {
          color: theme => theme.palette.color.field,
          border: theme => theme.palette.border.default,
          // borderRadius: 0,
          fontSize: 13,
          lineHeight: '28px',
          height: 28,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          fontFamily: 'Montserrat Regular',
          '*::before': {
            borderBottom: 'none !important',
          },
          '& > .MuiInputBase-input': {
            padding: '4px 8px',
            '-webkit-text-fill-color': theme => theme.palette.color.field,
          },
          '& input:-webkit-autofill': {
            '-webkit-box-shadow': theme => `0 0 0 100px ${theme.palette.common.black} inset`,
          },
        },
        '.MuiOutlinedInput-notchedOutline': {
          border: 'none !important',
        },
      }}
      {...props}
    />
  )
}

export const SearchAssistantFilterManager: React.FC<SearchAssistantFilterManagerProps> = props => {
  const {
    dialogId,
    createSearchPayload,
    mode = FavoritesFiltersTableMode.VIEW,
    entityObjectCode,
    searchAssistantId,
    searchAssistantKey,
    onChangeDirty,
  } = props

  const theme = useTheme()
  const { t } = useTranslation()

  const {
    data: savedSearchFilters = [],
    isLoading: isFirstLoadingSearchFilters,
    isFetching: isFetchingSearchFilters,
  } = useFetchSearchFiltersQuery(
    {
      objectCode: entityObjectCode,
      searchAssistantId,
    },
    {
      skip: !searchAssistantId || !entityObjectCode,
    }
  )

  const defaultSearchFilter = useAppSelector(state =>
    getDefaultSearchFilter(state, searchAssistantKey)
  )

  const isLoadingSavedSearchFilters = isFirstLoadingSearchFilters || isFetchingSearchFilters

  const searchAssistantPresenter = useMemo(
    () => new SearchAssistantPresenter(searchAssistantKey),
    [searchAssistantKey]
  )

  const { sort, orderedSearchFilters, handleNameColumnSort } = useSortSearchFilterManager({
    searchAssistantPresenter,
    savedSearchFilters,
  })

  const [tableMode, setTableMode] = useState<FavoritesFiltersTableMode>(mode)
  const [editSearchFilterTitle, setEditSearchFilterTitle] = useState(
    createSearchPayload?.title ?? ''
  )
  // Название такое же как и в SearchAssistant, но означает выбранный фильтр только в менеджере фильтров
  const [selectedSearchFilter, setSelectedSearchFilter] = useState<GETSearchType | null>(null)

  const isDirtySearchFilter = useMemo(() => {
    if (tableMode === FavoritesFiltersTableMode.CREATE) {
      return editSearchFilterTitle.trim() !== ''
    }

    if (tableMode === FavoritesFiltersTableMode.EDIT) {
      return editSearchFilterTitle.trim() !== selectedSearchFilter?.title
    }

    return false
  }, [tableMode, editSearchFilterTitle, selectedSearchFilter])

  const handleEditTableMode = () => {
    if (selectedSearchFilter) {
      setTableMode(FavoritesFiltersTableMode.EDIT)
      setEditSearchFilterTitle(selectedSearchFilter.title)

      return
    }
  }

  const handleCancelManage = () => {
    if (isDirtySearchFilter && !confirm(t('notifications.leave'))) {
      return
    }

    setTableMode(FavoritesFiltersTableMode.VIEW)
  }

  const handleEditSearchFilterTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditSearchFilterTitle(e.target.value)
  }

  const handleSelect =
    (searchFilter: GETSearchType, stopPropagation = false) =>
    (event: React.MouseEvent) => {
      if (stopPropagation) {
        event.stopPropagation()
      }

      if (tableMode !== FavoritesFiltersTableMode.VIEW) {
        return
      }

      if (selectedSearchFilter?.id === searchFilter.id) {
        setSelectedSearchFilter(null)

        return
      }

      setSelectedSearchFilter(prevSelectedSearchFilter =>
        prevSelectedSearchFilter?.id === searchFilter.id ? null : searchFilter
      )
    }

  const [isShowDeleteFavouriteSearchModal, setIsShowDeleteFavouriteSearchModal] =
    useState<boolean>(false)

  const handleToggleDeleteFavouriteSearchModal = () => {
    setIsShowDeleteFavouriteSearchModal(prev => !prev)
  }

  const handleCloseDialog = () => {
    searchAssistantPresenter.closeFilterManager(dialogId)
  }

  const { handleUpdateDefaultSearchFilter, isSettingDefaultSearchFilter } =
    useUpdateDefaultSearchFilterManager({
      updateDefaultSearchFilterPayload: {
        selectedSearchFilterId: selectedSearchFilter?.id,
        selectedSearchFilterTitle: selectedSearchFilter?.title,
        searchAssistantId,
        entityObjectCode,
      },
    })

  const { handleUpdateTitle, isUpdatingSearchFilterTitle } = useUpdateSearchFilterTitleManager({
    editSearchFilterTitle,
    onUpdateTitle: searchFilter => {
      setTableMode(FavoritesFiltersTableMode.VIEW)
      setSelectedSearchFilter(searchFilter)
      setEditSearchFilterTitle('')
    },
  })

  const { handleCreateSearchFilter, isLoadingCreateSearchFilter } = useCreateSearchFilterManager({
    searchFilterTitle: editSearchFilterTitle,
    onCreateFilter: () => {
      setTableMode(FavoritesFiltersTableMode.VIEW)
      setEditSearchFilterTitle('')
    },
  })

  const { handleDeleteSearchFilter, isDeletingSearchFilter } = useDeleteSearchFilterManager({
    selectedSearchFilterId: selectedSearchFilter?.id,
    onDeleteSearchFilter: () => {
      setSelectedSearchFilter(null)
      setEditSearchFilterTitle('')
      searchAssistantPresenter.saveSelectedFavouriteCriteria(null)
      setIsShowDeleteFavouriteSearchModal(false)
    },
  })

  const handleSaveOrRename = () => {
    if (selectedSearchFilter) {
      handleUpdateTitle(selectedSearchFilter.id)

      return
    }

    if (createSearchPayload) {
      handleCreateSearchFilter(createSearchPayload)
    }
  }

  usePrompt({ when: isDirtySearchFilter })

  useEffect(() => {
    onChangeDirty?.(isDirtySearchFilter)
  }, [isDirtySearchFilter])

  useEffect(() => {
    setEditSearchFilterTitle(createSearchPayload?.title || '')
  }, [createSearchPayload?.title])

  useEffect(() => {
    if (mode === FavoritesFiltersTableMode.VIEW) {
      setSelectedSearchFilter(defaultSearchFilter)
    } else {
      setSelectedSearchFilter(null)
    }
  }, [mode, defaultSearchFilter])

  const disabledSaveButtton =
    defaultSearchFilter?.id === selectedSearchFilter?.id ||
    isSettingDefaultSearchFilter ||
    isFetchingSearchFilters

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 140,
        p: '4px 6px 8px 6px',
      }}
    >
      <Box
        sx={{
          border: theme => `1px solid ${theme.palette.color.divider}`,
          ...FLEXIBLE,
          position: 'relative',
        }}
      >
        <Box
          p={'0 38px 0 21px !important'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px  !important',
            justifyContent: 'space-around',
            boxShadow: '0px 4px 4px 0px #00000040, 0px 4px 4px 0px #00000040',
            height: 24,
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover > .MuiIcon-root': {
              opacity: sort ? 1 : 0.5,
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Montserrat Regular',
              fontWeight: 600,
              fontSize: 13,
              lineHeight: '24px',
              color: theme => theme.palette.common.black,
            }}
            onClick={handleNameColumnSort}
          >
            {t('filter.favorites.dialog.columnName')}
          </Typography>
          <CustomSvgIcon
            src={sortArrowUp}
            sx={{
              height: '100%',
              display: 'flex',
              width: '10px',
              opacity: sort ? 1 : 0,
              transform: sort === Sort.Desc ? 'rotate(180deg)' : undefined,
            }}
          />
          <Typography
            sx={{
              fontFamily: 'Montserrat Regular',
              fontWeight: 600,
              fontSize: 13,
              lineHeight: '24px',
              color: theme => theme.palette.common.black,
            }}
          >
            {t('filter.favorites.dialog.columnDefault')}
          </Typography>
        </Box>
        <Box
          sx={{
            overflowY: 'auto',
            height: '100%',
            pt: 1,
          }}
        >
          {isLoadingSavedSearchFilters ? (
            <AppLoader size='1.5rem' />
          ) : (
            <Grid container alignContent={'flex-start'}>
              <Grid container item>
                {tableMode === FavoritesFiltersTableMode.CREATE && (
                  <Grid
                    container
                    sx={{
                      ...rowSx,
                      backgroundColor: theme => theme.palette.background.prevSelectedRow,
                      p: '0 50px 0 10px  !important',
                    }}
                  >
                    <EditTextField
                      disabled={isLoadingCreateSearchFilter}
                      value={editSearchFilterTitle}
                      onChange={handleEditSearchFilterTitle}
                    />
                    {isLoadingCreateSearchFilter && <AppLoader size='1rem' sx={appLoaderSx} />}
                  </Grid>
                )}
                {orderedSearchFilters.map(searchFilter => {
                  const isSelected = searchFilter.id === selectedSearchFilter?.id

                  return (
                    <Grid
                      key={searchFilter.id}
                      container
                      item
                      sx={{
                        ...rowSx,
                        height: 28,
                        '&:hover': {
                          bgcolor: theme => theme.palette.background.hovered,
                        },
                        ...(isSelected &&
                          tableMode === FavoritesFiltersTableMode.EDIT && {
                            p: '0 50px 0 10px',
                            backgroundColor: theme => theme.palette.background.prevSelectedRow,
                          }),
                      }}
                    >
                      {tableMode === FavoritesFiltersTableMode.EDIT && isSelected ? (
                        <EditTextField
                          disabled={isUpdatingSearchFilterTitle}
                          value={editSearchFilterTitle}
                          onChange={handleEditSearchFilterTitle}
                        />
                      ) : (
                        <ListItemButton
                          selected={isSelected}
                          sx={{
                            p: '0 75px 0 10px',
                            height: 28,
                            display: 'flex',
                            justifyContent: 'space-between',
                            '&.Mui-selected': {
                              bgcolor: theme => theme.palette.background.prevSelectedRow,
                            },
                          }}
                          onClick={handleSelect(searchFilter)}
                        >
                          <ListItemText
                            title={searchFilter.title}
                            sx={{
                              m: 0,
                              '& .MuiTypography-root': {
                                fontSize: 13,
                                lineHeight: '24px',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                fontFamily: 'Montserrat Regular',
                                color: theme => theme.palette.common.black,
                                whiteSpace: 'nowrap',
                              },
                            }}
                          >
                            {searchFilter.title}
                          </ListItemText>
                          <Radio
                            checked={isSelected}
                            size={'small'}
                            value={searchFilter.id}
                            sx={{
                              padding: 0,
                              '&.Mui-checked': {
                                color: (theme: Theme) => theme.palette.primary.main,
                              },
                            }}
                            onClick={handleSelect(searchFilter, true)}
                          />
                        </ListItemButton>
                      )}
                      {isSelected && (isDeletingSearchFilter || isUpdatingSearchFilterTitle) && (
                        <AppLoader size='1rem' sx={appLoaderSx} />
                      )}
                    </Grid>
                  )
                })}
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
      {tableMode === FavoritesFiltersTableMode.VIEW && (
        <Grid container columnSpacing='13px' justifyContent='flex-end' mt={2}>
          <Grid item>
            <StyledButton
              disabled={!selectedSearchFilter}
              theme={theme}
              startIcon={
                <CustomSvgIcon
                  src={pencil}
                  sx={{
                    opacity: !selectedSearchFilter ? 0.5 : 1,
                    height: '100%',
                    fontSize: '15px !important',
                  }}
                />
              }
              onClick={handleEditTableMode}
            >
              {t('filter.favorites.dialog.rename')}
            </StyledButton>
          </Grid>
          <Grid item>
            <StyledButton
              disabled={!selectedSearchFilter || isDeletingSearchFilter}
              theme={theme}
              startIcon={
                <CustomSvgIcon
                  src={close}
                  sx={{
                    opacity: !selectedSearchFilter || isDeletingSearchFilter ? 0.5 : 1,
                    height: '100%',
                    fontSize: '10px !important',
                  }}
                />
              }
              onClick={handleToggleDeleteFavouriteSearchModal}
            >
              {t('filter.favorites.dialog.delete')}
            </StyledButton>
          </Grid>
        </Grid>
      )}
      <ConfirmModal
        actionBtnText={t('modal.delete.btn')}
        isShow={isShowDeleteFavouriteSearchModal}
        text={t('modal.delete.warning')}
        title={t('modal.delete.title')}
        containerStyle={{
          px: 0.5,
          py: 0.5,
          // borderRadius: 0
        }}
        onClose={handleToggleDeleteFavouriteSearchModal}
        onConfirm={handleDeleteSearchFilter}
      />
      <Grid item sx={{ my: '14px' }}>
        <Divider sx={{ borderColor: theme => theme.palette.color.divider }} />
      </Grid>
      <Grid container item gap={3} justifyContent={'flex-end'}>
        {tableMode === FavoritesFiltersTableMode.VIEW ? (
          <Grid container gap={2} justifyContent={'flex-end'}>
            <Grid item>
              <StyledButton theme={theme} onClick={handleCloseDialog}>
                {t('filter.favorites.dialog.close')}
              </StyledButton>
            </Grid>
            <Grid item>
              <StyledButton
                disabled={disabledSaveButtton}
                theme={theme}
                onClick={handleUpdateDefaultSearchFilter}
              >
                {t('filter.favorites.dialog.save')}
              </StyledButton>
            </Grid>
          </Grid>
        ) : (
          <>
            <Grid item>
              <StyledButton
                disabled={!isDirtySearchFilter || isUpdatingSearchFilterTitle}
                theme={theme}
                onClick={handleSaveOrRename}
              >
                {t('filter.favorites.dialog.ok')}
              </StyledButton>
            </Grid>
            <Grid item>
              <StyledButton theme={theme} onClick={handleCancelManage}>
                {t('filter.favorites.dialog.cancel')}
              </StyledButton>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  )
}
